/**
 * Queries multiple ArcGIS feature service layers for attributes at a specific geographic point.
 *
 * This function sends parallel `query` requests to each specified layer ID within a feature service,
 * using the provided longitude and latitude as the query geometry. It returns a mapping of layer IDs
 * to arrays of feature attribute objects found at that location.
 */

import esriRequest from "@arcgis/core/request";
import { HttpProxyConfig } from "../types/config";
import { proxify } from "./proxy";

type LonLat = { longitude: number; latitude: number };

export async function queryLayersAtPoint(
  featureServiceBaseUrl: string,
  layerIds: number[],
  center: LonLat,
  proxy?: HttpProxyConfig,
  geometry?: any
): Promise<Record<number, any[]>> {
  const base = featureServiceBaseUrl.replace(/\/+$/, "");

  // Use full parcel geometry if provided, otherwise fall back to center point
  let geom: string;
  let geometryType: string;
  let inSR: string;

  if (geometry) {
    // Detect geometry type based on structure
    const hasRings = geometry.rings && Array.isArray(geometry.rings);
    const hasPaths = geometry.paths && Array.isArray(geometry.paths);
    const hasXY = typeof geometry.x === 'number' && typeof geometry.y === 'number';

    if (hasRings) {
      // It's a polygon
      geom = JSON.stringify(geometry);
      geometryType = "esriGeometryPolygon";
    } else if (hasPaths) {
      // It's a polyline
      geom = JSON.stringify(geometry);
      geometryType = "esriGeometryPolyline";
    } else if (hasXY) {
      // It's just a point, use it as-is
      geom = JSON.stringify(geometry);
      geometryType = "esriGeometryPoint";
    } else {
      // Unknown geometry, fall back to center point
      console.warn('Unknown geometry type, falling back to center point');
      geom = JSON.stringify({
        x: center.longitude,
        y: center.latitude,
        spatialReference: { wkid: 4326 }
      });
      geometryType = "esriGeometryPoint";
    }

    // Extract the spatial reference from the geometry
    const sr = geometry.spatialReference;
    inSR = sr?.wkid?.toString() || sr?.latestWkid?.toString() || "4326";
  } else {
    geom = JSON.stringify({
      x: center.longitude,
      y: center.latitude,
      spatialReference: { wkid: 4326 }
    });
    geometryType = "esriGeometryPoint";
    inSR = "4326";
  }

  const byLayer: Record<number, any[]> = {};
  await Promise.all(
    layerIds.map(async (id) => {
      const url = proxify(`${base}/${id}/query`, proxy);
      const params = new URLSearchParams({
        f: "json",
        where: "1=1",
        geometry: geom,
        geometryType: geometryType,
        inSR: inSR,
        spatialRel: "esriSpatialRelIntersects",
        outFields: "*",
        outSR: "4326",
        returnGeometry: "false"
      });

      // Use esriRequest so the ArcGIS JS API handles CORS automatically
      const requestOptions = geometry
        ? { method: "post" as const, body: params.toString(), headers: { "Content-Type": "application/x-www-form-urlencoded" }, responseType: "json" as const }
        : { query: Object.fromEntries(params), responseType: "json" as const };
      const response = await esriRequest(url, requestOptions);

      const json = response.data;
      byLayer[id] = (json.features ?? []).map((f: any) => f.attributes || {});
    })
  );
  return byLayer;
}